/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Flex,
  Group,
  HStack,
  IconButton,
  Input,
  InputAddon,
  Menu,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';
import { CaretDown, CheckCircle, File, Plus, UploadSimple, X } from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import {
  AnalyzeJobRequirementsResponse,
  apiAnalyzeJobRequirements,
  apiExtractJD,
  apiGetCurrencies,
  apiGetExtractorProviders,
  apiGetJobCategories,
  GetJobCategoriesResponse
} from '@/api/services';
import { Field } from '@/components/ui/field';
import { useCreateJob } from '@/store/createJob';
import { JobDescription } from '@/types/JobDescription';
import {
  EmploymentType,
  PromptKey,
  Stepper,
  WorkType,
  getEmploymentTypeLabel,
  getEmploymentTypeOptions,
  getWorkTypeLabel,
  getWorkTypeOptions,
  validateEmploymentType,
  validateWorkType
} from '@/types/enums';
import RichEditor from '@/components/forms/RichEditor';
import { LlmModelProvider, getLlmModelProviderSupportFileTypes } from '@/types/enums';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { getFileSize } from '@/utils';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

interface Provider {
  analysis_id: string | null;
  analysis_name: PromptKey;
  provider: LlmModelProvider | null;
  loading: boolean;
  error: string | null;
}

export default function EditJobDetails() {
  const createJobStore = useCreateJob();
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState(false);
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;
  const [loadingText, setLoadingText] = useState('');
  const [submittingTitle, setSubmittingTitle] = useState('');
  const [submittingMessage, setSubmittingMessage] = useState('');
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState<string>('');
  const [provider, setProvider] = useState<Provider>({
    analysis_id: null,
    analysis_name: PromptKey.JDExtractor,
    provider: null,
    loading: true,
    error: null
  });

  const defaultProvider: Provider = {
    analysis_id: null,
    analysis_name: PromptKey.JDExtractor,
    provider: null,
    loading: false,
    error: null
  };

  const fetchProviders = async () => {
    setProvider({ ...defaultProvider, loading: true, error: null });

    try {
      const response = await fetchAPI<AnalysisProvider[]>({
        request: apiGetExtractorProviders
      });

      if (response.success) {
        const providersData = response.data;
        const extractorProvider = providersData.find(
          (p) => p.analysis_name === PromptKey.JDExtractor
        );

        if (extractorProvider) {
          setProvider({
            ...extractorProvider,
            loading: false,
            error: null
          });
        } else {
          console.error('No extractor provider configured.');
          setProvider({
            ...defaultProvider,
            error: 'No extractor provider configured. Please contact support to enable file upload.'
          });
        }
      } else {
        console.error('Error fetching analysis providers:', response.message);
        setProvider({
          ...defaultProvider,
          error: 'Failed to load file upload options. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Unexpected error fetching analysis providers:', error);
      setProvider({
        ...defaultProvider,
        error: 'Failed to load file upload options. Please try again later.'
      });
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const retryProviderFetch = useCallback(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    if (!isFileUploading) return;

    const baseMessages = ['Uploading', 'Processing', 'Extracting', 'Analyzing', 'Summarizing'];
    let index = 0;
    let dotCount = 0;
    const maxDots = 3;
    const maxRepeats = 4;
    let repeatCount = 0;

    const updateLoadingText = () => {
      setLoadingText(`${baseMessages[index]}${'.'.repeat(dotCount)}`);
      dotCount = (dotCount + 1) % (maxDots + 1);
      if (dotCount === 0 && ++repeatCount === maxRepeats) {
        index = (index + 1) % baseMessages.length;
        repeatCount = 0;
      }
    };

    const intervalId = setInterval(updateLoadingText, 500);
    updateLoadingText();

    return () => clearInterval(intervalId);
  }, [isFileUploading]);

  const formatCurrency = (value: number): string => (value ? `$ ${value.toLocaleString()}` : '');

  const parseCurrencyToNumber = (value: string): number =>
    Number(value.replace(/[^0-9.-]+/g, '')) || 0;

  interface FormValues {
    jobTitle: string;
    category?: string;
    currency: { value: Currency };
    salaryFrom: string;
    salaryTo: string;
    employmentType?: { value: EmploymentType };
    workType?: { value: WorkType };
    location: string;
    jobDescription: string;
    clientContactName: string;
    clientContactRole: string;
    clientContactEmail: string;
    clientContactPhone: string;
  }

  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    setError,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      jobTitle: createJobStore.jobTitle,
      category: createJobStore.category || '',
      salaryFrom: createJobStore.salaryFrom ? formatCurrency(createJobStore.salaryFrom) : '',
      salaryTo: createJobStore.salaryTo ? formatCurrency(createJobStore.salaryTo) : '',
      employmentType: createJobStore.employmentType
        ? { value: createJobStore.employmentType }
        : undefined,
      workType: createJobStore.workType ? { value: createJobStore.workType } : undefined,
      location: createJobStore.location,
      jobDescription: createJobStore.jobDescription,
      clientContactName: createJobStore.contactName,
      clientContactRole: createJobStore.contactRole,
      clientContactEmail: createJobStore.contactEmail,
      clientContactPhone: createJobStore.contactPhone
    }
  });

  useEffect(() => {
    if (!isSubmitting) return;

    const baseTitles = [
      'Analyzing Job Description',
      'Scanning Job Description',
      'Finalizing Analysis'
    ];
    const baseMessages = [
      'Identifying key skills and requirements.',
      'Matching with industry standards.',
      'Preparing AI-driven insights.'
    ];
    let index = 0;
    let dotCount = 0;
    const maxDots = 3;
    const maxRepeats = 3;
    let repeatCount = 0;

    const updateSubmittingMessage = () => {
      setSubmittingTitle(`${baseTitles[index]}${'.'.repeat(dotCount)}`);
      setSubmittingMessage(baseMessages[index]);
      dotCount = (dotCount + 1) % (maxDots + 1);
      if (dotCount === 0 && ++repeatCount === maxRepeats) {
        index = (index + 1) % baseTitles.length;
        repeatCount = 0;
      }
    };

    const intervalId = setInterval(updateSubmittingMessage, 500);
    updateSubmittingMessage();

    return () => clearInterval(intervalId);
  }, [isSubmitting]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetchAPI<Currency[]>({
          request: apiGetCurrencies
        });

        if (response.success) {
          const currenciesData = response.data;
          setCurrencies(currenciesData);

          const usdCurrency = currenciesData.find((currency) => currency.code === 'USD');
          if (usdCurrency) {
            setValue('currency', { value: usdCurrency });
          }
        } else {
          console.error('Error fetching currencies:', response.message);
        }
      } catch (error) {
        console.error('Unexpected error fetching currencies:', error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetchAPI<GetJobCategoriesResponse>({
          request: () => apiGetJobCategories()
        });

        if (response.success && response.data) {
          setCategories(response.data.categories);
        } else {
          console.error('Error fetching categories:', response.message);
        }
      } catch (error) {
        console.error('Unexpected error fetching categories:', error);
      }
    };

    fetchCurrencies();
    fetchCategories();
  }, []);

  const handleAddNewCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()];
      setCategories(updatedCategories);
      setValue('category', newCategory.trim());
      setNewCategory('');
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    setAnalysisError(false);

    if (createJobStore.jdFile && createJobStore.jdFile.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      return;
    }

    const {
      jobTitle,
      category,
      currency,
      salaryFrom,
      salaryTo,
      employmentType,
      workType,
      location,
      jobDescription,
      clientContactName,
      clientContactRole,
      clientContactEmail,
      clientContactPhone
    } = formData;

    if (!employmentType || !workType) return;

    if ((salaryFrom && !salaryTo) || (!salaryFrom && salaryTo)) {
      setError('salaryFrom', {
        message: 'Both salary fields must be filled together'
      });
      setError('salaryTo', {
        message: 'Both salary fields must be filled together'
      });
      return;
    }

    const salaryFromValue = salaryFrom ? parseCurrencyToNumber(salaryFrom) : null;
    const salaryToValue = salaryTo ? parseCurrencyToNumber(salaryTo) : null;

    if ((salaryFromValue && salaryFromValue <= 0) || (salaryToValue && salaryToValue <= 0)) {
      if (salaryFromValue && salaryFromValue <= 0) {
        setValue('salaryFrom', '');
        await trigger('salaryFrom');
      }
      if (salaryToValue && salaryToValue <= 0) {
        setValue('salaryTo', '');
        await trigger('salaryTo');
      }
      return;
    }

    const updateJobStore = () => {
      createJobStore.setJobTitle(jobTitle);
      createJobStore.setCategory(category || '');
      createJobStore.setCurrency(currency.value);
      createJobStore.setSalaryFrom(salaryFromValue);
      createJobStore.setSalaryTo(salaryToValue);
      createJobStore.setEmploymentType(employmentType.value);
      createJobStore.setWorkType(workType.value);
      createJobStore.setLocation(location);
      createJobStore.setJobDescription(jobDescription);
      createJobStore.setContactName(clientContactName);
      createJobStore.setContactRole(clientContactRole);
      createJobStore.setContactEmail(clientContactEmail);
      createJobStore.setContactPhone(clientContactPhone);
    };

    updateJobStore();

    try {
      const response = await fetchAPI<AnalyzeJobRequirementsResponse>({
        request: () =>
          apiAnalyzeJobRequirements({
            payload: {
              job_title: jobTitle,
              job_description: jobDescription
            }
          })
      });

      if (response.success) {
        const { requirements, nice_to_haves, position_tags } = response.data;
        createJobStore.setRequirements(requirements);
        createJobStore.setNiceToHave(nice_to_haves);
        createJobStore.setPositionTags(position_tags);

        createJobStore.setCurrentPage(Stepper.AiAnalysis);
        if (!createJobStore.isStepEnabled(Stepper.AiAnalysis)) {
          createJobStore.setStep(Stepper.AiAnalysis);
        }
      } else {
        console.error('Error analyzing job description:', response.message);
        setAnalysisError(true);
      }
    } catch (error) {
      console.error('Error summarizing job description:', error);
      setAnalysisError(true);
    }
  });

  const handleBack = () => {
    createJobStore.setStep(Stepper.Client);
    createJobStore.setCurrentPage(Stepper.Client);
  };

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (provider?.error || provider?.loading) {
      setFileError('File upload is not available. Please wait or try again later.');
      return;
    }

    createJobStore.setJdFile(file);
    setIsFileUploading(true);
    setFileError(null);

    if (file.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      setIsFileUploading(false);
      return;
    }

    try {
      const response = await fetchAPI<JobDescription>({
        request: () => apiExtractJD({ payload: { file } })
      });

      if (response.success) {
        const {
          job_title,
          employment_type,
          work_type,
          location,
          job_description,
          contact_name,
          contact_role,
          contact_email,
          contact_phone,
          salary_currency,
          salary_from,
          salary_to
        } = response.data;

        const formattedJobDescription = job_description?.replace(/\\n/g, '\n') || '';

        const fieldsToUpdate = {
          jobTitle: job_title,
          location,
          jobDescription: formattedJobDescription,
          clientContactName: contact_name,
          clientContactRole: contact_role,
          clientContactEmail: contact_email,
          clientContactPhone: contact_phone,
          salaryFrom: formatCurrency(salary_from),
          salaryTo: formatCurrency(salary_to),
          employmentType:
            employment_type && validateEmploymentType(employment_type)
              ? { value: employment_type }
              : undefined,
          workType: work_type && validateWorkType(work_type) ? { value: work_type } : undefined
        };

        Object.entries(fieldsToUpdate).forEach(([key, value]) => {
          if (value !== undefined) setValue(key as keyof typeof fieldsToUpdate, value);
        });

        const currency = currencies.find((c) => c.code === salary_currency);
        if (currency) {
          setValue('currency', { value: currency });
        }

        await trigger();
      } else {
        console.error('Error extracting job description:', response.message);
        setFileError('File extraction failed. Please try again.');
      }
    } catch (error) {
      console.error('Error extracting job description:', error);
      setFileError('File extraction failed. Please try again.');
    } finally {
      setIsFileUploading(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      {isSubmitting ? (
        <Box
          w="100%"
          border="2px solid"
          borderRadius="12px"
          borderColor="neutrals.300"
          bg="background.secondary"
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py="240px"
        >
          <Stack align="center">
            <Spinner
              w="120px"
              h="120px"
              borderWidth="16px"
              color="primary.900"
              css={{ '--spinner-track-color': 'colors.gray.200' }}
              mb="40px"
            />
            <Box fontSize="36px" fontWeight="bold" color="neutrals.900">
              {submittingTitle}
            </Box>
            <Box fontSize="16px" color="neutrals.700">
              {submittingMessage}
            </Box>
          </Stack>
        </Box>
      ) : provider?.loading ? (
        <Box
          w="100%"
          border="2px solid"
          borderRadius="12px"
          borderColor="neutrals.300"
          bg="background.secondary"
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py="240px"
        >
          <Stack align="center">
            <Spinner
              w="80px"
              h="80px"
              borderWidth="8px"
              color="primary.900"
              css={{ '--spinner-track-color': 'colors.gray.200' }}
              mb="24px"
            />
            <Box fontSize="24px" fontWeight="bold" color="neutrals.900">
              Loading...
            </Box>
            <Box fontSize="14px" color="neutrals.600">
              Configuring your upload settings
            </Box>
          </Stack>
        </Box>
      ) : (
        <Box
          w="100%"
          border="2px solid"
          borderRadius="12px"
          borderColor="neutrals.300"
          bg="background.secondary"
        >
          <Box display="flex" flexDirection="column" p="32px">
            <Box fontWeight="bold" fontSize="36px" mb="8px">
              Define Job Details
            </Box>
            <Box color="neutrals.500" mb="40px">
              Provide a brief description of the role to get started.
            </Box>
            <Box fontWeight="bold" fontSize="24px" mb="24px">
              Job Details
            </Box>
            <Stack gap="16px" align="flex-start" width="100%">
              {provider.error ? (
                <Box
                  w="100%"
                  border="2px solid"
                  borderRadius="12px"
                  borderColor="error.300"
                  bg="error.50"
                  p="24px"
                  textAlign="center"
                >
                  <Text fontSize="18px" fontWeight="bold" color="error.700" mb="8px">
                    Upload Unavailable
                  </Text>
                  <Text fontSize="16px" color="error.600" mb="24px">
                    {provider.error}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={retryProviderFetch}
                    color="neutrals.700"
                    fontSize="16px"
                    borderColor="neutrals.500"
                    borderRadius="8px"
                    p="8px 24px"
                  >
                    Retry
                  </Button>
                </Box>
              ) : (
                <>
                  <Box>
                    <Box color="neutrals.800" fontSize="18px" mb="4px">
                      Upload a job description file, and we'll fill in the details for you.
                    </Box>
                    <Box color="neutrals.500" fontSize="16px">
                      {(() => {
                        const types = provider?.provider
                          ? getLlmModelProviderSupportFileTypes(provider.provider)
                          : ['.pdf'];
                        const isSingle = types.length === 1;
                        return (
                          <>
                            Supported {isSingle ? 'format' : 'formats'}: {types.join(', ')} (
                            {getFileSize(fileSizeLimit)} max)
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                  {createJobStore.jdFile || isFileUploading ? (
                    <Box display="flex" alignItems="center" width="100%">
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width="150px"
                        height="150px"
                        borderRadius="12px"
                        border="1px solid"
                        borderColor={fileError ? 'error.500' : 'primary.100'}
                        bg="primary.50"
                      >
                        {isFileUploading ? (
                          <>
                            <Spinner
                              size="lg"
                              borderWidth="5px"
                              color="primary.900"
                              css={{ '--spinner-track-color': 'colors.gray.200' }}
                            />
                            <Box
                              mt="8px"
                              fontSize="16px"
                              fontWeight="semibold"
                              color="neutrals.900"
                              textAlign="center"
                            >
                              {loadingText}
                            </Box>
                          </>
                        ) : (
                          <File size={64} color="#4e76ff" />
                        )}
                      </Box>
                      <Box ml="32px" color="neutrals.900">
                        <Box display="flex" alignItems="center" mb="4px">
                          {!isFileUploading && (
                            <Box mr="12px">
                              <CheckCircle size={24} color="#4e76ff" weight="fill" />
                            </Box>
                          )}
                          <Box fontSize="18px" color="neutrals.800" mr="16px">
                            {createJobStore.jdFile?.name}
                          </Box>
                          {!isFileUploading && (
                            <X
                              size={16}
                              color="#545454"
                              onClick={() => {
                                createJobStore.setJdFile(null);
                                setFileError(null);
                              }}
                              cursor="pointer"
                            />
                          )}
                        </Box>
                        <Box fontSize="16px" color={fileError ? 'error.700' : 'neutrals.500'}>
                          {getFileSize(createJobStore.jdFile?.size || 0)}
                        </Box>
                        {fileError && (
                          <Box fontSize="16px" color="error.700" mt="4px">
                            {fileError}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      w="100%"
                      h="200px"
                      border="1px dashed"
                      borderColor="neutrals.300"
                      borderRadius="12px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      _hover={{ borderColor: 'neutrals.300' }}
                      bg="neutrals.50"
                    >
                      <Box
                        position="relative"
                        width="100%"
                        height="200px"
                        border="2px dashed"
                        borderColor="neutrals.300"
                        borderRadius="12px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        _hover={{ borderColor: 'neutrals.300' }}
                        bg="neutrals.50"
                      >
                        <input
                          id="fileInput"
                          type="file"
                          accept={
                            provider?.provider
                              ? getLlmModelProviderSupportFileTypes(provider.provider).join(',')
                              : '.pdf'
                          }
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer'
                          }}
                          onChange={onUploadFile}
                          disabled={provider?.loading || isFileUploading || isSubmitting}
                        />
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <UploadSimple size={60} color="#404040" />
                          <Box textAlign="center" color="neutrals.900">
                            Drop file here or{' '}
                            <Box as="span" color="primary.900">
                              click to upload
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              )}
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Job Title
                  </Box>
                }
                invalid={!!errors.jobTitle}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.jobTitle?.message}
                  </Box>
                }
              >
                <Input
                  disabled={isFileUploading || isSubmitting}
                  size="lg"
                  borderRadius="8px"
                  placeholder="Enter job title"
                  {...register('jobTitle', {
                    required: 'Job title is required'
                  })}
                  _disabled={{
                    bg: 'neutrals.300'
                  }}
                />
              </Field>
              <Field
                label={<Box fontSize="16px">Category</Box>}
                invalid={!!errors.category}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.category?.message}
                  </Box>
                }
              >
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Menu.Root positioning={{ placement: 'bottom-start' }}>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          border="1px solid"
                          borderRadius="8px"
                          borderColor={errors.category ? 'error.500' : 'neutrals.200'}
                          w="100%"
                          textAlign="left"
                          fontSize="16px"
                          disabled={isFileUploading || isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            w="100%"
                            color={field.value ? 'neutrals.900' : 'neutrals.500'}
                          >
                            {field.value || 'Select a category (optional)'}
                            <CaretDown size={16} />
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content borderRadius="8px" style={{ zIndex: 1500 }}>
                          {categories.length > 0 && (
                            <>
                              {categories.map((category) => (
                                <Menu.Item
                                  key={category}
                                  value={category}
                                  cursor="pointer"
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                  onClick={() => {
                                    field.onChange(category);
                                  }}
                                >
                                  {category}
                                </Menu.Item>
                              ))}
                              <Menu.Separator />
                            </>
                          )}
                          <Menu.Item
                            value="new-category-input"
                            closeOnSelect={false}
                            _hover={{ bg: 'transparent' }}
                          >
                            <HStack w="100%" onClick={(e) => e.stopPropagation()}>
                              <Input
                                fontSize="16px"
                                placeholder="Enter new category"
                                value={newCategory}
                                size="sm"
                                onChange={(e) => setNewCategory(e.target.value)}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    handleAddNewCategory();
                                  }
                                }}
                              />
                              <IconButton
                                variant="ghost"
                                size="sm"
                                color="primary.900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddNewCategory();
                                }}
                                disabled={!newCategory.trim()}
                              >
                                <Plus size={16} weight="bold" />
                              </IconButton>
                            </HStack>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              <Flex gap="10px" alignItems="flex-start" w="100%">
                <Box flex="1">
                  <Field
                    label={<Box fontSize="16px">Salary</Box>}
                    invalid={!!errors.salaryFrom || !!errors.salaryTo}
                    errorText={
                      <Box fontSize="14px" color="error.700">
                        {errors.salaryFrom?.message || errors.salaryTo?.message}
                      </Box>
                    }
                  >
                    <Stack direction="row" width="100%" gap="8px">
                      {['salaryFrom', 'salaryTo'].map((field, index) => (
                        <Group key={field} attached width="100%">
                          <InputAddon fontSize="16px" bg="background.secondary">
                            {index === 0 ? 'From' : 'To'}
                          </InputAddon>
                          <Input
                            disabled={isFileUploading || isSubmitting}
                            size="lg"
                            placeholder={index === 0 ? '$50,000' : '$100,000'}
                            width="100%"
                            type="text"
                            borderRadius="8px"
                            _disabled={{
                              bg: 'neutrals.300'
                            }}
                            {...register(field as 'salaryFrom' | 'salaryTo')}
                            onChange={(e) => {
                              const inputValue = e.target.value.trim();
                              if (!inputValue || inputValue === '$') {
                                e.target.value = '';
                                return;
                              }

                              let formattedValue = inputValue.startsWith('$')
                                ? inputValue.slice(1)
                                : inputValue;
                              const lastCharacter = formattedValue.slice(-1);

                              if (isNaN(Number(lastCharacter))) {
                                formattedValue = formattedValue.slice(0, -1);
                              }

                              if (formattedValue) {
                                const numericValue = Number(formattedValue.replace(/,/g, ''));
                                e.target.value = '$ ' + numericValue.toLocaleString();
                              } else {
                                e.target.value = '';
                              }
                            }}
                          />
                        </Group>
                      ))}
                    </Stack>
                  </Field>
                </Box>
                <Box w="280px">
                  <Field
                    label={<Box fontSize="16px">Salary Currency</Box>}
                    invalid={!!errors.currency}
                    errorText={
                      <Box fontSize="14px" color="error.700">
                        {errors.currency?.message}
                      </Box>
                    }
                  >
                    <Controller
                      name="currency"
                      control={control}
                      rules={{ required: 'Currency is required' }}
                      render={({ field }) => (
                        <Menu.Root>
                          <Menu.Trigger asChild>
                            <Button
                              variant="outline"
                              size="lg"
                              borderColor={errors.currency ? 'error.500' : 'neutrals.200'}
                              borderRadius="8px"
                              disabled={isFileUploading || isSubmitting}
                              _disabled={{
                                bg: 'neutrals.300'
                              }}
                              w="280px"
                            >
                              <Flex justifyContent="space-between" alignItems="center" width="100%">
                                <Box
                                  fontSize="16px"
                                  color="neutrals.700"
                                  overflow="hidden"
                                  whiteSpace="nowrap"
                                  textOverflow="ellipsis"
                                >
                                  {field.value?.value
                                    ? `${field.value.value.code} - ${field.value.value.name}`
                                    : 'Select currency'}
                                </Box>
                                <Box as="span" ml="8px" mr="0">
                                  <CaretDown size={16} />
                                </Box>
                              </Flex>
                            </Button>
                          </Menu.Trigger>
                          <Menu.Positioner>
                            <Menu.Content minW="10rem">
                              <Menu.RadioItemGroup
                                value={field.value?.value.code || ''}
                                onValueChange={(value) => {
                                  const selectedCurrency = currencies.find(
                                    (item) => item.code === value.value
                                  );
                                  field.onChange(
                                    selectedCurrency ? { value: selectedCurrency } : null
                                  );
                                }}
                              >
                                {currencies.map(({ code, name }) => (
                                  <Menu.RadioItem
                                    key={code}
                                    value={code}
                                    fontSize="16px"
                                    _hover={{
                                      cursor: 'pointer',
                                      bg: 'neutrals.100'
                                    }}
                                  >
                                    {`${code} - ${name}`}
                                  </Menu.RadioItem>
                                ))}
                              </Menu.RadioItemGroup>
                            </Menu.Content>
                          </Menu.Positioner>
                        </Menu.Root>
                      )}
                    />
                  </Field>
                </Box>
              </Flex>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Employment Type
                  </Box>
                }
                invalid={!!errors.employmentType}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.employmentType?.message}
                  </Box>
                }
              >
                <Controller
                  name="employmentType"
                  control={control}
                  rules={{ required: 'Employment type is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          borderRadius="8px"
                          borderColor={errors.employmentType ? 'error.500' : 'neutrals.200'}
                          disabled={isFileUploading || isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                        >
                          <Flex justifyContent="space-between" alignItems="center" width="100%">
                            <Box fontSize="16px" color="neutrals.700">
                              {field.value
                                ? getEmploymentTypeLabel(field.value.value)
                                : 'Select employment type'}
                            </Box>
                            <Box as="span" ml="auto" mr="0">
                              <CaretDown size={16} />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content minW="10rem">
                          <Menu.RadioItemGroup
                            value={field.value ? field.value.value : ''}
                            onValueChange={field.onChange}
                          >
                            {getEmploymentTypeOptions().map((item) => (
                              <Menu.RadioItem
                                key={item.value}
                                value={item.value}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {item.label}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Work Type
                  </Box>
                }
                invalid={!!errors.workType}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.workType?.message}
                  </Box>
                }
              >
                <Controller
                  name="workType"
                  control={control}
                  rules={{ required: 'Work type is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          borderRadius="8px"
                          borderColor={errors.workType ? 'error.500' : 'neutrals.200'}
                          disabled={isFileUploading || isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                        >
                          <Flex justifyContent="space-between" alignItems="center" width="100%">
                            <Box fontSize="16px" color="neutrals.700">
                              {field.value
                                ? getWorkTypeLabel(field.value.value)
                                : 'Select work type'}
                            </Box>
                            <Box as="span" ml="auto" mr="0">
                              <CaretDown size={16} />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content minW="10rem" zIndex={1500}>
                          <Menu.RadioItemGroup
                            value={field.value ? field.value.value : ''}
                            onValueChange={field.onChange}
                          >
                            {getWorkTypeOptions().map((item) => (
                              <Menu.RadioItem
                                key={item.value}
                                value={item.value}
                                fontSize="16px"
                                _hover={{
                                  cursor: 'pointer',
                                  bg: 'neutrals.100'
                                }}
                              >
                                {item.label}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Location
                  </Box>
                }
                invalid={!!errors.location}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.location?.message}
                  </Box>
                }
              >
                <Input
                  disabled={isFileUploading || isSubmitting}
                  _disabled={{
                    bg: 'neutrals.300'
                  }}
                  size="lg"
                  borderRadius="8px"
                  placeholder="Enter location"
                  {...register('location', {
                    required: 'Location is required'
                  })}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Job Description
                  </Box>
                }
                invalid={!!errors.jobDescription}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.jobDescription?.message}
                  </Box>
                }
              >
                <Box
                  w="100%"
                  h="100%"
                  border="1px solid"
                  borderColor="neutrals.200"
                  borderRadius="8px"
                  p="16px 24px"
                  bg={isFileUploading || isSubmitting ? 'neutrals.100' : 'transparent'}
                >
                  <RichEditor
                    key="job-description"
                    content={watch('jobDescription')}
                    type="job-description"
                    placeholder="Briefly describe the role, including key responsibilities, required qualifications, and any other relevant details (e.g., job expectations, benefits, or work conditions)."
                    onChange={(content) => {
                      setValue('jobDescription', content);
                    }}
                    readOnly={isFileUploading || isSubmitting}
                    className={isFileUploading || isSubmitting ? 'read-only' : ''}
                  />
                </Box>
              </Field>
            </Stack>
            <Box h="40px" />
            <Box fontWeight="bold" fontSize="24px" mb="24px">
              Client Contact
            </Box>
            <Stack gap="16px" align="flex-start" width="100%">
              <Stack direction="row" width="100%" gap="8px">
                <Field
                  label={<Box fontSize="16px">Name</Box>}
                  invalid={!!errors.clientContactName}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactName?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter client name"
                    {...register('clientContactName')}
                  />
                </Field>
                <Field
                  label={<Box fontSize="16px">Role</Box>}
                  invalid={!!errors.clientContactRole}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactRole?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter role"
                    {...register('clientContactRole')}
                  />
                </Field>
              </Stack>
              <Stack direction="row" width="100%" gap="8px">
                <Field
                  label={<Box fontSize="16px">Email</Box>}
                  invalid={!!errors.clientContactEmail}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactEmail?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter email"
                    {...register('clientContactEmail', {
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                </Field>
                <Field
                  label={<Box fontSize="16px">Phone</Box>}
                  invalid={!!errors.clientContactPhone}
                  errorText={
                    <Box fontSize="14px" color="error.700">
                      {errors.clientContactPhone?.message}
                    </Box>
                  }
                >
                  <Input
                    disabled={isFileUploading || isSubmitting}
                    _disabled={{
                      bg: 'neutrals.300'
                    }}
                    size="lg"
                    borderRadius="8px"
                    placeholder="Enter phone"
                    {...register('clientContactPhone')}
                  />
                </Field>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}
      {analysisError && (
        <Box fontSize="16px" color="error.700" mt="16px">
          Error analyzing job description. Please try again.
        </Box>
      )}
      <Flex justifyContent="flex-end" align="right" mt="64px">
        <Button
          disabled={isFileUploading || isSubmitting || provider.loading}
          w="120px"
          h="48px"
          borderRadius="8px"
          borderColor="primary.900"
          color="primary.900"
          bg="background.secondary"
          fontSize="16px"
          fontWeight="regular"
          mr="16px"
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          disabled={isFileUploading || provider.loading}
          w="120px"
          h="48px"
          borderRadius="8px"
          bg="primary.900"
          fontSize="16px"
          fontWeight="regular"
          type="submit"
          loading={isSubmitting}
        >
          Next
        </Button>
      </Flex>
    </form>
  );
}
